import { forwardRef } from 'react';
import { cn } from '@/hen-ui/utils';
import { useIsClamped } from '@/hen-ui/hooks';
import { CardRoot } from '@/hen-ui/composites/Card/Card';
import { CollapsibleCard } from '@/hen-ui/composites/CollapsibleCard';
import { sponsoredCardVariants } from './sponsored-card.variants';
import { SponsoredCardHeader } from './SponsoredCardHeader';
import { SponsoredCardIdentity } from './SponsoredCardIdentity';
import { SponsoredCardBody } from './SponsoredCardBody';
import { SponsoredVisitButton } from './SponsoredVisitButton';
import type { SponsoredCardProps } from './types';

export const SponsoredCard = forwardRef<HTMLDivElement, SponsoredCardProps>(
  (
    {
      sponsorLabel,
      visitHref,
      visitLabel,
      onVisitClick,
      sponsoredVisit,
      logoSrc,
      logoAlt,
      name,
      nameHref,
      onNameClick,
      metaItems,
      aboutHeading,
      children,
      expanded = false,
      onExpandedChange,
      modalOpen = false,
      onModalOpenChange,
      className,
      ...props
    },
    ref,
  ) => {
    const hasBlurb = children != null;
    const { ref: blurbRef, isClamped } = useIsClamped(!expanded);
    const cardClassName = cn(sponsoredCardVariants(), className);

    const headerNode = (
      <SponsoredCardHeader
        sponsorLabel={sponsorLabel}
        visitHref={visitHref}
        visitLabel={visitLabel}
        onVisitClick={onVisitClick}
        sponsoredVisit={sponsoredVisit}
      />
    );

    const identityNode = (
      <SponsoredCardIdentity
        logoSrc={logoSrc}
        logoAlt={logoAlt}
        name={name}
        nameHref={nameHref}
        onNameClick={onNameClick}
        metaItems={metaItems}
      />
    );

    const mobileVisitCta = (
      <SponsoredVisitButton
        visitHref={visitHref}
        visitLabel={visitLabel}
        onVisitClick={onVisitClick}
        sponsoredVisit={sponsoredVisit}
        className='w-full shrink-0 px-md md:hidden'
      />
    );

    if (!hasBlurb) {
      return (
        <CardRoot
          ref={ref}
          className={cardClassName}
          role='region'
          aria-label={`${name} sponsored school card`}
          {...props}
        >
          {headerNode}
          {identityNode}
          {mobileVisitCta}
        </CardRoot>
      );
    }

    const drawerVisitCta = (
      <SponsoredVisitButton
        visitHref={visitHref}
        visitLabel={visitLabel}
        onVisitClick={onVisitClick}
        sponsoredVisit={sponsoredVisit}
        className='w-full shrink-0 px-md'
      />
    );

    return (
      <CollapsibleCard
        ref={ref}
        className={cardClassName}
        ariaLabel={`${name} sponsored school card`}
        expanded={expanded}
        onExpandedChange={onExpandedChange}
        modalOpen={modalOpen}
        onModalOpenChange={onModalOpenChange}
        footerClassName='pt-xxs w-full'
        showToggle={expanded || modalOpen || isClamped}
        renderHeader={() => (
          <>
            {headerNode}
            {identityNode}
            {mobileVisitCta}
          </>
        )}
        renderBody={(isExpanded) => (
          <SponsoredCardBody
            aboutHeading={aboutHeading}
            expanded={isExpanded}
            blurbRef={blurbRef}
          >
            {children}
          </SponsoredCardBody>
        )}
        renderDrawerHeader={() => (
          <>
            <SponsoredCardHeader
              sponsorLabel={sponsorLabel}
              visitHref={visitHref}
              visitLabel={visitLabel}
              onVisitClick={onVisitClick}
              sponsoredVisit={sponsoredVisit}
              hideVisitButton
            />
            {identityNode}
            {drawerVisitCta}
          </>
        )}
        renderDrawerBody={() => (
          <SponsoredCardBody aboutHeading={aboutHeading} expanded unclamped>
            {children}
          </SponsoredCardBody>
        )}
        drawerHeaderClassName='flex flex-col gap-md px-lg pt-md pb-0'
        {...props}
      />
    );
  },
);

SponsoredCard.displayName = 'SponsoredCard';
